import {
  BookingActionTypes,
  bookingAddToCalendar,
  bookingCancelFlights,
  bookingCancelFlightsSuccess,
  bookingCancelShowModal,
  bookingCancelWithPaymentPaymentSuccess,
  bookingRefundPayments,
  bookingCancelZeroBalance,
  bookingCheckPaymentsStatus,
  bookingCheckPaymentsStatusSuccess,
  bookingCommitBooking,
  bookingCommitBookingSuccess,
  bookingCompleteBooking,
  bookingCompleteBookingPendingCustomerAction,
  bookingCompleteBookingSuccess,
  bookingDeleteBooking,
  bookingDeleteBookingSuccess,
  bookingGetBooking,
  bookingGetBookingSuccess,
  bookingGetSsrAvailability,
  bookingGetSsrAvailabilitySuccess,
  bookingPaymentsAccepted,
  bookingPaymentsAcceptedBookingUpdated,
  bookingRetrieveBooking,
  bookingRetrieveBookingNoNavigation,
  bookingRetrieveBookingSuccess,
  bookingRetrieveBookingError,
  bookingSetOriginalBooking,
  bookingUpdateBooking,
  bookingUpdateBookingSuccess,
  saveGoogleBoardingPassToWallet,
  bookingCreateGoogleBoardingPassSuccess,
  saveAppleBoardingPassToWallet,
  bookingCreateAppleBoardingPassSuccess,
  bookingClearOriginalBooking,
  bookingCreateAppleBoardingPassesSuccess,
  bookingCreateGoogleBoardingPassesSuccess,
  saveAppleBoardingPassesToWallet,
  saveGoogleBoardingPassesToWallet,
  bookingRetrieveOriginalBooking,
  bookingStoreBooking,
  bookingRetrieveBookingDeeplinkBags,
  bookingRetrieveBookingDeeplinkSeats,
  bookingRetrieveBookingDeeplinkMeals,
  bookingRetrieveBookingDeeplinkExtras,
  bookingRetrieveBookingDeeplinkPay,
  bookingRetrieveTokenError,
  bookingSetTokenGetBooking,
  bookingTripDetailsInit,
  tripDisruptionCheckSuccess,
  bookingPaymentsDeclined,
  upgradeToEconomyClassicAction,
  upgradeToEconomyClassicActionSuccess,
  bookingCancelModification,
  bookingGetBookingAfterPassengerUpdateSuccess,
  checkCreditCardFeeSucceed,
  setBookingError, resetCreditCardFeeSucceed
} from '@actions/booking.action';
import { appGetTokenInfoSuccess, appNoOp, onError } from '@actions/app.action';
import { inject, Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { BookingService } from '@services/api-clients/booking.service';
import { Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import { BaseError, ErrorType } from '@models/app.models';
import {
  PaymentActionTypes,
  paymentApplyCreditFileSuccess,
  paymentApplyVoucherSuccess,
  paymentRefundPaymentsSuccess,
  paymentSubmitPaymentsSuccess,
} from '@actions/payment.action';
import {
  PassengerActionTypes,
  updatePassengersAssistanceSuccess,
  updatePassengersDocumentsSuccess,
  updatePassengersSuccess,
  updateTripsPassengersSuccess,
} from '@actions/passenger.action';
import {
  availabilitySellTripSelectionSuccess,
  availabilityStoreAvailabilitySelections
} from '@actions/availability.action';
import { SsrService } from '@services/api-clients/ssr.service';
import { initUpgradeFareToPremiumSuccess, updatePassengerSeatsSuccess } from '@actions/seat.action';
import { bagsSellBagSsrsSuccess } from '@actions/bags.action';
import {
  ExtrasActionTypes,
  extrasSellExtrasBundleSuccess,
  extrasSellExtrasSsrsSuccess,
} from '@actions/extras.action';
import { mealsSellMealsSsrSuccess } from '@actions/meals.action';
import {
  selectBooking,
  selectBookingCurrency,
  selectBookingJourneys,
  selectBookingPrimaryContact,
  selectConfirmationNumber,
  selectOriginalBooking,
} from '@selectors/booking.selector';
import {
  CultureKeyType,
  CULTURE_ROUTE_PARAM,
  FALLBACK_CURRENCY,
  FlowBaseUrlEnum,
  AGENT_AUTHENTICATION_ERROR_CODE,
  TOKEN_TIMEOUT_ERROR_CODE, routeDiscountMap,
} from '@constants/app.constants';
import { saveAs } from 'file-saver';
import { BookingPaymentStatus } from '@models/booking.models';
import { WindowService } from '@services/window/window.service';
import { PaymentService } from '@services/api-clients/payment.service';
import {
  selectIsBookPath,
  selectRouteParam,
  selectUrl,
} from '@selectors/router.selector';
import { getRootPath } from '@utils/data-utils';
import {
  CheckInActionTypes,
  checkInAutoAssignSeatsSuccess,
} from '@actions/check-in.action';
import { ModalService } from '@services/modal/modal.service';
import { ConfirmCancelFlightModalComponent } from 'src/app/features/payment/confirm-cancel-flight-modal/confirm-cancel-flight-modal.component';
import { selectPaymentCopy } from '@selectors/cms.selector';
import { WalletService } from '@services/api-clients/wallet.service';
import { navigateToPage } from '@actions/navigate.action';
import { FlowPathEnum } from '@models/flow-wrapper.model';
import { sendPageViewEvent } from '@actions/gtm.action';
import { selectPaymentFlow } from '@selectors/payment.selector';
import { UpliftService } from '@services/uplift/uplift.service';
import { PaymentFlow } from '@models/payment.model';
import {
  selectAvailabilitySelectedState,
  selectAvailabilityTripTypeState,
} from '@selectors/availability.selector';
import { N0AvailableJourney } from '@models/responses/availability-response.model';
import { N0SellKey } from '@models/requests/trip-sell-request.model';
import { TripType } from '@constants/home.constants';
import { CabinProductClass } from '@constants/cabin.constants';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { StandardBagSsrs } from '@constants/bags.constants';
import { paymentModalId } from "@constants/payment.constant";
import posthog from 'posthog-js';

@Injectable()
export class BookingEffect {
  private readonly gbs = inject(GrowthBookService);

  private readonly gbf = GrowthBookFeatureKeys;
  readonly TripType = TripType;

  constructor(
    private actions$: Actions,
    private bookingService: BookingService,
    private paymentService: PaymentService,
    private modalService: ModalService,
    private ssrService: SsrService,
    private walletService: WalletService,
    private store: Store,
    @Inject(DOCUMENT) private doc: Document,
    private upliftService: UpliftService
  ) {}

  gtmPageViewed$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(sendPageViewEvent),
      concatLatestFrom(() => [
        this.store.select(selectOriginalBooking),
        this.store.select(selectIsBookPath),
      ]),
      mergeMap(([, originalBooking, isBookPath]) => {
        if (originalBooking && isBookPath) {
          return of(bookingClearOriginalBooking());
        }
        return of(appNoOp());
      }),
    );
  });

  getBooking$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        bookingGetBooking,
        availabilitySellTripSelectionSuccess,
        bagsSellBagSsrsSuccess,
        checkInAutoAssignSeatsSuccess,
        extrasSellExtrasSsrsSuccess,
        mealsSellMealsSsrSuccess,
        updatePassengersSuccess,
        bookingCompleteBookingSuccess,
        bookingUpdateBookingSuccess,
        updatePassengerSeatsSuccess,
        paymentApplyVoucherSuccess,
        paymentApplyCreditFileSuccess,
        bookingPaymentsAccepted,
        bookingPaymentsDeclined,
        extrasSellExtrasBundleSuccess,
        initUpgradeFareToPremiumSuccess,
        updatePassengersDocumentsSuccess,
        checkCreditCardFeeSucceed,
        resetCreditCardFeeSucceed
      ),
      mergeMap((action) => {
        this.modalService.hide(paymentModalId);
        return this.bookingService.getBookingInState().pipe(
          switchMap(({ data: booking }) => {
            let bookingCopy = {...booking};

            if (
              this.gbs.growthBook.isOn(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT) &&
              this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT, '') === 'b') {
                bookingCopy = {
                  ...booking,
                  bags: booking.bags?.map(b => ({
                    ...b,
                    passengerBags: b.passengerBags.map(pb => ({
                      ...pb,
                      bags: pb.bags.map(bag => ({
                        ...bag,
                        inBundleCount: bag.ssrCode === StandardBagSsrs.CARRYON_BAG && booking.journeys.find(j => j.journeyKey === b.journeyKey)?.productClass === CabinProductClass.ECONOMY_LIGHT ? bag.inBundleCount + 1 : bag.inBundleCount
                      }))
                    }))
                  }))
                }
            }

          const origin = booking.journeys[0]?.designator.origin;
          const destination = booking.journeys[0]?.designator.destination;
          if (origin && destination && booking.journeys.length > 1) {
            const route = `${origin}-${destination}`
            const discount = routeDiscountMap[route] ?? 0.20;
            const {fares} = booking.tripBreakdown.categoryTotals;
            const updatedFareDetails = fares.fareDetails.map((fareDetail) => {
              const newStrikethroughTotalPrice = fareDetail.details.reduce((acc, detail) => {
                const modifiedPrice = detail.code === null ? detail.totalPrice / (1 - discount) : detail.totalPrice;
                return detail.code === 'PROMO' ? acc : acc + modifiedPrice;
              }, 0);

              return {
                ...fareDetail,
                strikethroughTotalPrice: newStrikethroughTotalPrice,
              };
            });

            const totalStrikethroughPrice = updatedFareDetails.reduce((acc, price) => acc + price.strikethroughTotalPrice, 0);

            const updatedFares = {
              ...fares,
              fareDetails: updatedFareDetails,
              strikethroughTotalPrice: totalStrikethroughPrice,
            };

            const {journeyBreakdowns} = booking.tripBreakdown;
            const updatedJourneyBreakdowns = journeyBreakdowns.map((breakdown) => {
              const updatedFareDetails = breakdown.categoryBreakdown.fares.fareDetails.map((fareDetail) => {
                const newStrikethroughTotalPrice = fareDetail.details.reduce((acc, detail) => {
                  const modifiedPrice = detail.code === null ? detail.totalPrice / (1 - discount) : detail.totalPrice;
                  return detail.code === 'PROMO' ? acc : acc + modifiedPrice;
                }, 0);

                return {
                  ...fareDetail,
                  strikethroughTotalPrice: newStrikethroughTotalPrice,
                };
              });

              const updatedFares = {
                ...fares,
                fareDetails: updatedFareDetails,
                strikethroughTotalPrice: totalStrikethroughPrice,
              };

              const updatedCategoryBreakdown = {
                ...breakdown.categoryBreakdown,
                fares: updatedFares,
              };

              const journeyTotalStrikethrough = updatedFareDetails.reduce((acc, price) => acc + price.strikethroughTotalPrice, 0);

              return {
                ...breakdown,
                journeyTotalStrikethrough,
                categoryBreakdown: updatedCategoryBreakdown,
              };
            });


            const balanceDueStrikethrough =
              (bookingCopy.tripBreakdown.categoryTotals.bags?.totalPrice ?? 0) +
              (bookingCopy.tripBreakdown.categoryTotals.extras?.totalPrice ?? 0) +
              (bookingCopy.tripBreakdown.categoryTotals.meals?.totalPrice ?? 0) +
              (bookingCopy.tripBreakdown.categoryTotals.other?.totalPrice ?? 0) +
              (bookingCopy.tripBreakdown.categoryTotals.seats?.totalPrice ?? 0) +
              (bookingCopy.tripBreakdown.categoryTotals.spoilage?.totalPrice ?? 0) +
              updatedFares.strikethroughTotalPrice;

            bookingCopy = {
              ...booking,
              tripBreakdown: {
                ...booking.tripBreakdown,
                balanceDueStrikethrough,
                categoryTotals: {
                  ...booking.tripBreakdown.categoryTotals,
                  fares: updatedFares,
                },
                journeyBreakdowns: updatedJourneyBreakdowns,
              },
            };
          }

            if (action.type === BookingActionTypes.bookingPaymentsAccepted) {
              return of(
                bookingPaymentsAcceptedBookingUpdated({
                  booking: bookingCopy,
                }))
            }
            if (
              (action.type ===
                PassengerActionTypes.UpdatePassengersDocumentsSuccess &&
                action.flow === FlowBaseUrlEnum.CHECK_IN &&
                this.gbs.isVariantOfExperimentEnabled(
                  'feat-002688-online-check-in',
                  true,
                )) ||
              (action.type === BookingActionTypes.getBooking &&
                action.doccheck &&
                action.flow === FlowBaseUrlEnum.TRIPS &&
                this.gbs.isVariantOfExperimentEnabled(
                  'feat-002815-doccheck-popup-MMB',
                  'b',
                )) ||
              (action.type === PassengerActionTypes.updatePassengersSuccess &&
                ((action.flow === FlowBaseUrlEnum.BOOK &&
                  this.gbs.isVariantOfExperimentEnabled(
                    'feat-002814-doccheck-popup-booking-flow',
                    'b',
                  )) ||
                  (action.flow === FlowBaseUrlEnum.MODIFY &&
                    this.gbs.isVariantOfExperimentEnabled(
                      'feat-002815-doccheck-popup-MMB',
                      'b',
                    ))))
            ) {
              return of(
                bookingGetBookingAfterPassengerUpdateSuccess({
                  booking: bookingCopy,
                }),
              );
            } else {
              return of(
                bookingGetBookingSuccess({
                  booking: bookingCopy,
                  navigate:
                    action.type === ExtrasActionTypes.sellExtrasSsrsSuccess,
                }),
              );
            }
          }),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.BookingFetchFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  confirmUpliftPayment$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(bookingPaymentsAcceptedBookingUpdated),
        concatLatestFrom(() => [this.store.select(selectPaymentFlow)]),
        tap(([booking, paymentFlow]) =>{
          if (paymentFlow === PaymentFlow.UPLIFT_LOAN && booking.booking.recordLocator) {
            this.upliftService.confirmPayment(booking.booking.recordLocator)
          }
        })
      )
    },
    {
      dispatch: false,
    })

  getBookingAppStart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appGetTokenInfoSuccess),
      mergeMap(({ info }) => {
        if (!info.hasBookingInState) {
          return of(bookingSetTokenGetBooking({ getBooking: false }));
        }
        return of(bookingSetTokenGetBooking({ getBooking: true }));
      }),
    );
  });

  commitBooking$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingCommitBooking),
      mergeMap(() => {
        return this.bookingService.commitBooking().pipe(
          switchMap(() => of(bookingCommitBookingSuccess())),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.BookingCommitFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  checkBookingPaymentsStatus$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingCheckPaymentsStatus),
      concatLatestFrom(() => this.store.select(selectUrl)),
      mergeMap(([, url]) => {
        const flow = getRootPath(url);
        return this.bookingService.getBookingPaymentStatus(flow).pipe(
          switchMap(({ data }) => {
            return of(bookingCheckPaymentsStatusSuccess({ data }));
          }),
          catchError(() => {
            return of(
              onError({
                error: new BaseError(ErrorType.BookingPollingFailure),
              }),
            );
          }),
        );
      }),
    );
  });

  pendingCustomerAction$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(bookingCompleteBookingPendingCustomerAction),
        tap(({ redirectUrl }) => {
          WindowService.goTo(redirectUrl);
        }),
      );
    },
    { dispatch: false },
  );

  addPayments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(paymentSubmitPaymentsSuccess),
      concatLatestFrom(() => [this.store.select(selectBooking)]),
      mergeMap(([, booking]) => {
        if (booking?.recordLocator) {
          return of(bookingUpdateBooking());
        }
        return of(bookingCompleteBooking());
      }),
    );
  });

  completeBooking$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingCompleteBooking),
      mergeMap(() => {
        return this.bookingService.completeBooking().pipe(
          switchMap(({ data }) => {
            return of(bookingCompleteBookingSuccess({ data }));
          }),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.BookingCompleteFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  handlePaymentsStatusChanged$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        bookingCompleteBookingSuccess,
        bookingUpdateBookingSuccess,
        bookingCheckPaymentsStatusSuccess,
      ),
      tap(({ data: { paymentStatus, redirectUrl3ds }, triggeredByAction }) => {
        posthog.capture('booking_payment_status_changed', {
          message: `Payment status changed to ${paymentStatus}`,
          category: 'booking',
          data: {
            paymentStatus,
            redirectUrl3ds,
            triggeredByAction
          }
        });
      }),
      mergeMap(
        ({ data: { paymentStatus, redirectUrl3ds }, triggeredByAction }) => {
          switch (paymentStatus) {
            case BookingPaymentStatus.Approved:
              return of(bookingPaymentsAccepted());
            case BookingPaymentStatus.Declined:
              return of(
                bookingPaymentsDeclined({
                  error: new BaseError(
                    triggeredByAction ===
                    PaymentActionTypes.refundPaymentsSuccess
                      ? ErrorType.BookingRefundFailure
                      : ErrorType.BookingPaymentsDeclined,
                  ),
                }),
              );
            case BookingPaymentStatus.PendingCustomerAction:
              return of(
                bookingCompleteBookingPendingCustomerAction({
                  redirectUrl: redirectUrl3ds,
                }),
              );
            default:
              console.warn(
                `unexpected booking payment status: ${paymentStatus}`,
              );
              return of(appNoOp());
          }
        },
      ),
    );
  });

  updateBooking$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        bookingUpdateBooking,
        updatePassengersAssistanceSuccess,
        updateTripsPassengersSuccess,
        paymentRefundPaymentsSuccess,
        bookingCancelZeroBalance,
        bookingCancelWithPaymentPaymentSuccess,
        checkInAutoAssignSeatsSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(selectUrl),
        this.store.select(selectConfirmationNumber),
        this.store.select(selectBookingPrimaryContact),
      ]),
      mergeMap(([action, url, confirmationNumber, primaryContact]) => {
        const flow = getRootPath(url);
        const doNotifyContacts =
          action.type === BookingActionTypes.updateBooking;
        return this.bookingService.updateBooking(flow, doNotifyContacts).pipe(
          switchMap(({ data }) => {
            // booking update success triggers next step navigation, which we don't
            // want when updating from the trip details page.
            switch (action.type) {
              case PassengerActionTypes.updateTripsPassengersSuccess:
                return flow === FlowBaseUrlEnum.CHECK_IN
                  ? of(updatePassengersDocumentsSuccess({flow}))
                  : of(bookingGetBooking({doccheck: true, flow}));
              case CheckInActionTypes.autoAssignSeatsSuccess:
                if (!primaryContact || !confirmationNumber) {
                  return of(appNoOp());
                }
                return of(
                  bookingRetrieveOriginalBooking({
                    confirmationNumber,
                    lastName: primaryContact.name.last,
                  }),
                );
            }
            return of(
              bookingUpdateBookingSuccess({
                data,
                triggeredByAction: action.type,
              }),
            );
          }),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.BookingCommitFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  getSsrAvailability$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingGetSsrAvailability, bookingRetrieveBookingSuccess),
      concatLatestFrom(() => this.store.select(selectBookingCurrency)),
      mergeMap(([action, currencyCode]) => {
        if (
          action.type === BookingActionTypes.retrieveBookingSuccess &&
          !action.next
        ) {
          return of(appNoOp());
        }
        return this.ssrService
          .getAvailability(currencyCode ?? FALLBACK_CURRENCY)
          .pipe(
            switchMap((response) => {
              return of(bookingGetSsrAvailabilitySuccess({ payload: response.data }));
            }),
            catchError(() =>
              of(
                onError({
                  error: new BaseError(
                    ErrorType.BookingGetSsrAvailabilityFailure,
                  ),
                }),
              ),
            ),
          );
      }),
    );
  });

  retrieveBooking$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingRetrieveBooking),
      mergeMap((action) => {
        return this.bookingService
          .getRetrieveBooking(action.confirmationNumber, action.lastName)
          .pipe(
            switchMap(({ data: booking }) => {
              return of(
                bookingRetrieveBookingSuccess({ booking, next: action.next }),
              );
            }),
            catchError((error: { code: string }) => {
              if (
                error.code === AGENT_AUTHENTICATION_ERROR_CODE ||
                (error.code === TOKEN_TIMEOUT_ERROR_CODE && !action.fromError)
              ) {
                //reattempt to get booking
                return of(
                  bookingRetrieveTokenError({
                    next: { ...action, fromError: true } as Action,
                  }),
                );
              }

              return of(bookingRetrieveBookingError());
            }),
          );
      }),
    );
  });

  retrieveBookingSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingRetrieveBookingSuccess),
      mergeMap((action) => {
        if (action.next) {
          return of(action.next);
        }

        return of(
          navigateToPage({
            flow: FlowBaseUrlEnum.TRIPS,
            page: FlowPathEnum.TRIP_DETAILS,
          }),
        );
      }),
    );
  });

  retrieveBookingError$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingRetrieveBookingError),
      mergeMap(() => {
        if (
          this.gbs.growthBook.isOn('feat-003240-your-trips-page-improvement') &&
          this.gbs.growthBook.getFeatureValue('feat-003240-your-trips-page-improvement', '') === 'b' ||
          this.gbs.growthBook.getFeatureValue('feat-003240-your-trips-page-improvement', '') === 'c') 
        {
          return of(setBookingError())
        } else {
          return of(
            onError({
              error: new BaseError(ErrorType.BookingRetrieveFailure),
            }),
          );
        }
      }),
    );
  });

  retrieveBookingNoNavigation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        bookingRetrieveBookingNoNavigation,
        bookingRetrieveBookingDeeplinkBags,
        bookingRetrieveBookingDeeplinkSeats,
        bookingRetrieveBookingDeeplinkMeals,
        bookingRetrieveBookingDeeplinkExtras,
        bookingRetrieveBookingDeeplinkPay,
      ),
      concatLatestFrom(() => this.store.select(selectOriginalBooking)),
      mergeMap(([action, originalBooking]) => {
        return this.bookingService
          .getRetrieveBooking(action.confirmationNumber, action.lastName)
          .pipe(
            switchMap(({ data: booking }) => {
              return of(bookingStoreBooking({ payload: booking }));
            }),
            catchError((error: { code: string }) => {
              if (
                error.code === AGENT_AUTHENTICATION_ERROR_CODE ||
                (error.code === TOKEN_TIMEOUT_ERROR_CODE && !action.fromError)
              ) {
                //reattempt to get booking
                return of(
                  bookingRetrieveTokenError({
                    next: { ...action, fromError: true } as Action & {
                      fromError: boolean;
                    },
                  }),
                );
              }

              return of(bookingRetrieveBookingError());
            }),
          );
      }),
    );
  });

  retrieveOriginalBooking$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingRetrieveOriginalBooking),
      mergeMap((action) => {
        return this.bookingService
          .getRetrieveBooking(action.confirmationNumber, action.lastName)
          .pipe(
            switchMap(({ data: booking }) => {
              return of(bookingSetOriginalBooking({ booking }));
            }),
            catchError(() =>
              of(bookingRetrieveBookingError()),
            ),
          );
      }),
    );
  });

  addToCalendar$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(bookingAddToCalendar),
        exhaustMap(() =>
          this.bookingService.addToCalendar().pipe(
            map((resp) => {
              saveAs(
                new Blob([resp.data.icsCode], { type: 'text/calendar' }),
                resp.data.fileName,
              );
              return appNoOp();
            }),
            catchError(() =>
              of(
                onError({
                  error: new BaseError(ErrorType.BookingCalendarInviteFailure),
                }),
              ),
            ),
          ),
        ),
      );
    },
    { dispatch: false },
  );

  cancelFlights$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingCancelFlights),
      mergeMap(({ journeyKeys }) => {
        return this.bookingService.cancelFlights(journeyKeys).pipe(
          switchMap(({ data: booking }) =>
            of(
              bookingCancelFlightsSuccess({
                booking,
              }),
            ),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.BookingCancelFlightsFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  refundPayments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingRefundPayments),
      mergeMap(() => {
        return this.paymentService.refundPayments().pipe(
          switchMap(() => of(paymentRefundPaymentsSuccess())),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.BookingRefundFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  showConfirmCancelModal$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingCancelShowModal),
      concatLatestFrom(() => this.store.select(selectPaymentCopy)),
      mergeMap(([action, copy]) => {
        if (!copy) {
          return of(appNoOp());
        }

        return (
          this.modalService
            .show(ConfirmCancelFlightModalComponent, {
              initialState: {
                cancelCmsCopy: copy,
              },
            })
            .content?.cancelOrContinue.pipe(
            switchMap((isContinue) => {
              if (!isContinue) {
                return of(appNoOp());
              }
              return of(action.next);
            }),
          ) ?? of(appNoOp())
        );
      }),
    );
  });

  deleteBooking$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingDeleteBooking),
      mergeMap(() =>
        this.bookingService.deleteBookingInState().pipe(
          map(() => bookingDeleteBookingSuccess()),
          catchError(() =>
            of(
              onError({ error: new BaseError(ErrorType.BookingDeleteFailure) }),
            ),
          ),
        ),
      ),
    );
  });

  saveGoogleBoardingPassToWallet$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveGoogleBoardingPassToWallet),
      concatLatestFrom(
        () =>
          this.store.select(
            selectRouteParam(CULTURE_ROUTE_PARAM),
          ) as Observable<CultureKeyType | undefined>,
      ),
      mergeMap(([action, cultureCode]) => {
        return this.walletService
          .createGoogleBoardingPass(action.boardingPassRequest, cultureCode)
          .pipe(
            map(({ data: boardingPassResponse }) =>
              bookingCreateGoogleBoardingPassSuccess({
                boardingPassRequest: action.boardingPassRequest,
                boardingPassResponse,
              }),
            ),
          );
      }),
    );
  });

  saveAppleBoardingPassToWallet$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveAppleBoardingPassToWallet),
      concatLatestFrom(
        () =>
          this.store.select(
            selectRouteParam(CULTURE_ROUTE_PARAM),
          ) as Observable<CultureKeyType | undefined>,
      ),
      mergeMap(([action, cultureCode]) => {
        return this.walletService
          .createAppleBoardingPass(action.boardingPassRequest, cultureCode)
          .pipe(
            map(({ data: boardingPassResponse }) =>
              bookingCreateAppleBoardingPassSuccess({
                boardingPassRequest: action.boardingPassRequest,
                boardingPassResponse,
              }),
            ),
          );
      }),
    );
  });

  saveGoogleBoardingPassesToWallet$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveGoogleBoardingPassesToWallet),
      concatLatestFrom(
        () =>
          this.store.select(
            selectRouteParam(CULTURE_ROUTE_PARAM),
          ) as Observable<CultureKeyType | undefined>,
      ),
      mergeMap(([action, cultureCode]) => {
        return this.walletService
          .createGoogleBoardingPasses(action.boardingPassRequests, cultureCode)
          .pipe(
            map(({ data: boardingPassResponse }) =>
              bookingCreateGoogleBoardingPassesSuccess({
                boardingPassRequests: action.boardingPassRequests,
                boardingPassResponse,
              }),
            ),
          );
      }),
    );
  });

  saveAppleBoardingPassesToWallet$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveAppleBoardingPassesToWallet),
      concatLatestFrom(
        () =>
          this.store.select(
            selectRouteParam(CULTURE_ROUTE_PARAM),
          ) as Observable<CultureKeyType | undefined>,
      ),
      mergeMap(([action, cultureCode]) => {
        return this.walletService
          .createAppleBoardingPasses(action.boardingPassRequests, cultureCode)
          .pipe(
            map(({ data: boardingPassResponse }) =>
              bookingCreateAppleBoardingPassesSuccess({
                boardingPassRequests: action.boardingPassRequests,
                boardingPassResponse,
              }),
            ),
          );
      }),
    );
  });

  openBoardingPassJwt$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          bookingCreateGoogleBoardingPassSuccess,
          bookingCreateGoogleBoardingPassesSuccess,
        ),
        tap((action) => {
          WindowService.open(
            `https://pay.google.com/gp/v/save/${action.boardingPassResponse.boardingPassJwt}`,
            '_blank',
          );
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  openBoardingPassDataUrl$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          bookingCreateAppleBoardingPassSuccess,
          bookingCreateAppleBoardingPassesSuccess,
        ),
        tap((action) => {
          const fileLink = this.doc.createElement('a');
          fileLink.href = action.boardingPassResponse.base64PassZipData;
          fileLink.download =
            action.type ===
            BookingActionTypes.bookingCreateAppleBoardingPassSuccess
              ? 'norse_pass.pkpass'
              : 'norse_pass.pkpasses';
          this.doc.body.append(fileLink);
          fileLink.click();
          this.doc.body.removeChild(fileLink);
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  tripDetailsDisruptionCheck$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingTripDetailsInit),
      mergeMap(() => {
        return this.bookingService.checkDisruption().pipe(
          switchMap((response) => {
            return of(
              tripDisruptionCheckSuccess({
                isDisrupted: response.data.activeSession,
                disruptionUrl: response.data.sessionToken,
              }),
            );
          }),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.BookingDisruptionFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  upgradeToEconomyClassic$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(upgradeToEconomyClassicAction),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySelectedState),
        this.store.select(selectAvailabilityTripTypeState),
        this.store.select(selectBookingJourneys)
      ]),
      mergeMap(([action, selection, tripType, bookingJourneys]) => {
        const updatedSellKeys: N0SellKey[] = [];
        if (selection && tripType) {
          const outboundJourneys: N0AvailableJourney | null | undefined =
            selection.journeys[0];
          const outboundSellKey: N0SellKey | null | undefined =
            selection.sellKeys[0];
          const foundedData = outboundJourneys?.fares.find(
            fare => fare.details.bundleCode === CabinProductClass.ECONOMY_CLASSIC
          );
          const isEconomyLight = bookingJourneys?.[0]?.productClass === CabinProductClass.ECONOMY_LIGHT

          if (foundedData && outboundSellKey && bookingJourneys?.[0]) {
            const economyClassicSellKey: N0SellKey = {
              journeyKey: outboundSellKey.journeyKey,
              fareAvailabilityKey: foundedData.fareAvailabilityKey,
              bundleCode: foundedData.details.bundleCode,
              originalBundleCode: bookingJourneys?.[0].productClass,
              originalJourneyKey :bookingJourneys?.[0].journeyKey,
            };
            updatedSellKeys.push(isEconomyLight ? {...economyClassicSellKey} : {...outboundSellKey});
          }

          if (tripType === this.TripType.roundTrip) {
            const returnJourneys: N0AvailableJourney | null | undefined =
              selection.journeys[1];
            const returnSellKey: N0SellKey | null | undefined =
              selection.sellKeys[1];

            const foundedData = returnJourneys?.fares.find(
              fare => fare.details.bundleCode === CabinProductClass.ECONOMY_CLASSIC
            );

            const isEconomyLightForReturn = bookingJourneys?.[1]?.productClass === CabinProductClass.ECONOMY_LIGHT

            if (foundedData && returnSellKey && bookingJourneys?.[1]) {
              const economyClassicSellKey: N0SellKey = {
                journeyKey: returnSellKey.journeyKey,
                fareAvailabilityKey: isEconomyLightForReturn ? foundedData.fareAvailabilityKey : returnSellKey.fareAvailabilityKey,
                bundleCode: isEconomyLightForReturn ? foundedData.details.bundleCode : returnSellKey.bundleCode,
                originalBundleCode: bookingJourneys?.[1].productClass,
                originalJourneyKey: bookingJourneys?.[1].journeyKey,
              };
              updatedSellKeys.push(isEconomyLightForReturn ? {...economyClassicSellKey} : {...returnSellKey});
            }
          }

          this.store.dispatch(
            availabilityStoreAvailabilitySelections({
              sellKeys: updatedSellKeys,
              journeys: selection.journeys
            }),
          )
        }
        return of(upgradeToEconomyClassicActionSuccess({sellKeys: updatedSellKeys, redirect: action.redirect}));
      }),
    );
  });

  cancelModification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingCancelModification),
      mergeMap(() => {
        return of(
          navigateToPage({
            flow: FlowBaseUrlEnum.TRIPS,
            page: FlowPathEnum.TRIP_DETAILS,
          }),
        );
      }),
    );
  });
}
